/**
 * User Role Constants
 * 
 * Defines all user roles in the multi-tenant revenue collection platform.
 * Use these constants instead of magic strings to prevent typos and enable
 * IDE autocomplete.
 * 
 * Role Hierarchy (highest to lowest):
 * 1. SUPER_ADMIN - Root platform administrator
 * 2. PLATFORM_ADMIN - Delegated platform administrator
 * 3. PLATFORM_REPORT_USER - Platform report runner/viewer
 * 4. STATE_ADMIN - State government administrator who owns a tenant
 * 5. ADMIN - Tenant staff with full operational access
 * 6. VIEWER - Read-only access for reporting and auditing
 * 
 * @module Constants
 */

export const USER_ROLES = {
  /**
   * Platform Super Administrator
   * - Can create and manage all tenants
   * - Can create STATE_ADMIN users
   * - Has access to platform-wide analytics
   */
  SUPER_ADMIN: 'SUPER_ADMIN',

  PLATFORM_ADMIN: 'PLATFORM_ADMIN',

  PLATFORM_REPORT_USER: 'PLATFORM_REPORT_USER',

  /**
   * State Administrator
   * - Owns and manages a specific tenant
   * - Can create ADMIN and VIEWER users within their tenant
   * - Full access to tenant configuration
   * - Can use State-scoped demand notice generation when not limited to specific LGAs
   */
  STATE_ADMIN: 'STATE_ADMIN',

  /**
   * Tenant Administrator
   * - Full operational access within assigned tenant
   * - Can manage revenue collection activities
   * - Cannot modify tenant settings or create users
   * - If assigned to specific LGAs, demand notice generation is limited to LGA scope
   */
  ADMIN: 'ADMIN',

  /**
   * Read-Only Viewer
   * - Can view data within assigned tenant
   * - Cannot create, update, or delete records
   * - Intended for auditors and supervisors
   */
  VIEWER: 'VIEWER',

  FIELD_AGENT: 'FIELD_AGENT',
  ENFORCEMENT_SUPERVISOR: 'ENFORCEMENT_SUPERVISOR',
  AUDITOR: 'AUDITOR',
  OPERATIONS_OFFICER: 'OPERATIONS_OFFICER'
};

export const PLATFORM_PERMISSIONS = [
  { key: 'canManagePlatformUsers', label: 'Manage Platform Users' },
  { key: 'canManageTenants', label: 'Manage States/Tenants' },
  { key: 'canViewPlatformAnalytics', label: 'View Platform Analytics' },
  { key: 'canViewPlatformAuditLogs', label: 'View Platform Audit Logs' },
  { key: 'canViewOwnerReports', label: 'View Owner Reports' },
  { key: 'canRunOwnerReports', label: 'Run Owner Reports' },
  { key: 'canManageOwnerReportConfigs', label: 'Manage Owner Report Configs' },
  { key: 'canManageTenantCommercialTerms', label: 'Manage Commercial Terms' },
];

export const PLATFORM_USER_ROLES = [
  USER_ROLES.SUPER_ADMIN,
  USER_ROLES.PLATFORM_ADMIN,
  USER_ROLES.PLATFORM_REPORT_USER,
];

export const PLATFORM_ASSIGNABLE_ROLES = [
  USER_ROLES.SUPER_ADMIN,
  USER_ROLES.PLATFORM_ADMIN,
  USER_ROLES.PLATFORM_REPORT_USER,
];

export default USER_ROLES;
